import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { CreditCard, Plus, Trash2, Check, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";



const SavedCardsSelector = ({ 
  email, 
  name, 
  onCardSelect, 
  selectedCardId,
  disabled = false 
}) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingCard, setDeletingCard] = useState(null);

  useEffect(() => {
    if (email) {
      fetchCards();
    } else {
      setLoading(false);
    }
  }, [email]);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers/cards/${encodeURIComponent(email)}`);
      setCards(response.data.cards || []);
      // Auto-select default card or "new" if no cards
      const defaultCard = response.data.cards?.find(c => c.is_default);
      if (defaultCard && !selectedCardId) {
        onCardSelect(defaultCard.id);
      } else if (!response.data.cards?.length && !selectedCardId) {
        onCardSelect("new");
      }
    } catch (error) {
      console.error("Error fetching cards:", error);
      onCardSelect("new");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!confirm("Are you sure you want to remove this card?")) return;
    
    setDeletingCard(cardId);
    try {
      await axios.delete(`${API}/customers/cards/${encodeURIComponent(email)}/${cardId}`);
      setCards(cards.filter(c => c.id !== cardId));
      if (selectedCardId === cardId) {
        onCardSelect("new");
      }
      toast.success("Card removed");
    } catch (error) {
      toast.error("Failed to remove card");
    } finally {
      setDeletingCard(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
        <span className="ml-2 text-stone-500">Loading payment options...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="text-emerald-950 font-medium block">Payment Method</label>
      
      {/* Saved Cards */}
      {cards.length > 0 && (
        <div className="space-y-2">
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() => !disabled && onCardSelect(card.id)}
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedCardId === card.id
                  ? "border-lime-500 bg-lime-50"
                  : "border-stone-200 hover:border-stone-300 bg-white"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-stone-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-950 capitalize">
                    {card.brand} •••• {card.last4}
                  </p>
                  <p className="text-sm text-stone-500">
                    Expires {card.exp_month.toString().padStart(2, '0')}/{card.exp_year}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedCardId === card.id && (
                  <div className="w-6 h-6 bg-lime-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCard(card.id);
                  }}
                  disabled={deletingCard === card.id}
                  className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                >
                  {deletingCard === card.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Card Option */}
      <div
        onClick={() => !disabled && onCardSelect("new")}
        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
          selectedCardId === "new"
            ? "border-lime-500 bg-lime-50"
            : "border-dashed border-stone-300 hover:border-stone-400 bg-white"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
          <Plus className="w-5 h-5 text-stone-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-emerald-950">
            {cards.length > 0 ? "Use a different card" : "Add payment card"}
          </p>
          <p className="text-sm text-stone-500">
            {cards.length > 0 ? "Enter new card details at checkout" : "Securely enter your card details"}
          </p>
        </div>
        {selectedCardId === "new" && (
          <div className="w-6 h-6 bg-lime-500 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Info text */}
      <p className="text-xs text-stone-500 text-center">
        Your card will be saved for faster checkout next time
      </p>
    </div>
  );
};

export default SavedCardsSelector;
